import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';

const CANVAS_WIDTH = 320;
const CANVAS_HEIGHT = 240;
const PLAYER_WIDTH = 20;
const PLAYER_HEIGHT = 8;
const PLAYER_Y = CANVAS_HEIGHT - 18;
const PLAYER_SPEED = 4;
const BULLET_SPEED = 5;
const ENEMY_BULLET_SPEED = 3;
const SHOOT_COOLDOWN_MS = 350;
const ENEMY_ROWS = 4;
const ENEMY_COLS = 8;
const ENEMY_WIDTH = 18;
const ENEMY_HEIGHT = 12;
const ENEMY_PADDING_X = 10;
const ENEMY_PADDING_Y = 10;
const ENEMY_OFFSET_TOP = 24;
const ENEMY_OFFSET_LEFT = (CANVAS_WIDTH - (ENEMY_COLS * (ENEMY_WIDTH + ENEMY_PADDING_X) - ENEMY_PADDING_X)) / 2;
const ENEMY_DROP = 10;
const START_LIVES = 3;
const TICK_MS = 33;
const ENEMY_FIRE_CHANCE = 0.02;

const ROW_COLORS = ['#ffb000', '#33ff66', '#33ff66', '#1fae4a'];
const ROW_POINTS = [30, 20, 20, 10];

interface Enemy {
  x: number;
  y: number;
  alive: boolean;
  color: string;
  points: number;
}

interface Bullet {
  x: number;
  y: number;
  vy: number;
}

type GameStatus = 'playing' | 'won' | 'lost';

@Component({
  selector: 'app-invaders-game',
  host: {
    role: 'dialog',
    'aria-modal': 'true',
    'aria-label': 'Invaders mini-game',
    '(document:keydown)': 'onKeydown($event)',
    '(document:keyup)': 'onKeyup($event)'
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="invaders-panel">
      <div class="invaders-header">
        <span class="invaders-title">*** INVADERS.EXE ***</span>
        <span class="invaders-score">SCORE: {{ score() }}</span>
        <span class="invaders-lives">LIVES: {{ lives() }}</span>
        <button class="invaders-close" type="button" (click)="requestClose()">[ESC] CLOSE</button>
      </div>
      <canvas #canvasEl [width]="canvasWidth" [height]="canvasHeight" class="invaders-canvas"></canvas>
      @if (status() !== 'playing') {
        <div class="invaders-gameover">
          {{ status() === 'won' ? 'INVASION REPELLED!' : 'EARTH HAS FALLEN' }} — SCORE {{ score() }} — PRESS ENTER TO RESTART
        </div>
      } @else {
        <div class="invaders-hint">ARROWS / A,D MOVE — SPACE TO FIRE — ESC TO EXIT</div>
      }
    </div>
  `,
  styles: [`
    :host {
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      background: rgba(0, 0, 0, 0.85);
      overflow: auto;
    }

    .invaders-panel {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      padding: 16px;
      background: #010c05;
      border: 2px solid #33ff66;
      border-radius: 4px;
      box-shadow: 0 0 24px rgba(51, 255, 102, 0.45);
      font-family: 'IBM Plex Mono', 'Courier New', monospace;
      color: #33ff66;
    }

    .invaders-header {
      display: flex;
      align-items: center;
      gap: 18px;
      width: 100%;
      justify-content: space-between;
      flex-wrap: wrap;
    }

    .invaders-title {
      font-weight: bold;
      letter-spacing: 2px;
      color: #ffb000;
      text-shadow: 0 0 6px rgba(255, 176, 0, 0.7);
    }

    .invaders-score,
    .invaders-lives {
      font-size: 0.9rem;
    }

    .invaders-close {
      background: transparent;
      border: 1px solid #33ff66;
      color: #33ff66;
      font-family: inherit;
      padding: 4px 10px;
      cursor: pointer;
      border-radius: 2px;
    }

    .invaders-close:hover {
      background: rgba(51, 255, 102, 0.15);
    }

    .invaders-canvas {
      width: auto;
      height: min(360px, calc(100vh - 180px));
      max-width: calc(100vw - 64px);
      image-rendering: pixelated;
      background: #000;
      border: 1px solid rgba(51, 255, 102, 0.5);
    }

    .invaders-gameover,
    .invaders-hint {
      font-size: 0.85rem;
      text-align: center;
      opacity: 0.9;
    }
  `]
})
export class InvadersGame implements OnInit, OnDestroy {
  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvasEl');
  private ctx: CanvasRenderingContext2D | null = null;
  private intervalId: ReturnType<typeof setInterval> | undefined;
  private readonly pressedKeys = new Set<string>();
  private lastShotAt = 0;
  private tickCount = 0;
  private walkFrame = false;

  readonly canvasWidth = CANVAS_WIDTH;
  readonly canvasHeight = CANVAS_HEIGHT;

  readonly close = output<void>();
  readonly score = signal(0);
  readonly lives = signal(START_LIVES);
  readonly status = signal<GameStatus>('playing');

  private playerX = (CANVAS_WIDTH - PLAYER_WIDTH) / 2;
  private enemies: Enemy[] = [];
  private enemyDirection = 1;
  private playerBullets: Bullet[] = [];
  private enemyBullets: Bullet[] = [];

  ngOnInit(): void {
    this.resetState();
    const canvas = this.canvasRef().nativeElement;
    this.ctx = canvas.getContext('2d');
    this.intervalId = setInterval(() => this.tick(), TICK_MS);
  }

  ngOnDestroy(): void {
    if (this.intervalId !== undefined) {
      clearInterval(this.intervalId);
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.requestClose();
      return;
    }
    if (event.key === 'Enter' && this.status() !== 'playing') {
      this.restart();
      return;
    }
    if (event.code === 'Space') {
      event.preventDefault();
      this.shoot();
      return;
    }
    const key = event.key.toLowerCase();
    if (['arrowleft', 'arrowright', 'a', 'd'].includes(key)) {
      event.preventDefault();
      this.pressedKeys.add(key);
    }
  }

  onKeyup(event: KeyboardEvent): void {
    this.pressedKeys.delete(event.key.toLowerCase());
  }

  requestClose(): void {
    this.close.emit();
  }

  private restart(): void {
    this.resetState();
  }

  private resetState(): void {
    this.playerX = (CANVAS_WIDTH - PLAYER_WIDTH) / 2;
    this.enemies = [];
    for (let row = 0; row < ENEMY_ROWS; row++) {
      for (let col = 0; col < ENEMY_COLS; col++) {
        this.enemies.push({
          x: ENEMY_OFFSET_LEFT + col * (ENEMY_WIDTH + ENEMY_PADDING_X),
          y: ENEMY_OFFSET_TOP + row * (ENEMY_HEIGHT + ENEMY_PADDING_Y),
          alive: true,
          color: ROW_COLORS[row % ROW_COLORS.length],
          points: ROW_POINTS[row % ROW_POINTS.length]
        });
      }
    }
    this.enemyDirection = 1;
    this.playerBullets = [];
    this.enemyBullets = [];
    this.score.set(0);
    this.lives.set(START_LIVES);
    this.status.set('playing');
    this.pressedKeys.clear();
    this.tickCount = 0;
    this.walkFrame = false;
  }

  private tick(): void {
    if (this.status() === 'playing') {
      this.tickCount++;
      if (this.tickCount % 15 === 0) {
        this.walkFrame = !this.walkFrame;
      }
      this.updatePlayer();
      this.moveEnemies();
      this.enemyFire();
      this.updateBullets();
      this.checkCollisions();
      this.checkEndConditions();
    }
    this.draw();
  }

  private updatePlayer(): void {
    if (this.pressedKeys.has('arrowleft') || this.pressedKeys.has('a')) {
      this.playerX = Math.max(0, this.playerX - PLAYER_SPEED);
    }
    if (this.pressedKeys.has('arrowright') || this.pressedKeys.has('d')) {
      this.playerX = Math.min(CANVAS_WIDTH - PLAYER_WIDTH, this.playerX + PLAYER_SPEED);
    }
  }

  private enemySpeed(): number {
    const total = ENEMY_ROWS * ENEMY_COLS;
    const aliveCount = this.enemies.filter((e) => e.alive).length;
    const destroyedRatio = 1 - aliveCount / total;
    return 0.6 + destroyedRatio * 2.8;
  }

  private moveEnemies(): void {
    const alive = this.enemies.filter((e) => e.alive);
    if (alive.length === 0) {
      return;
    }
    const speed = this.enemySpeed();
    const dx = this.enemyDirection * speed;
    const minX = Math.min(...alive.map((e) => e.x));
    const maxX = Math.max(...alive.map((e) => e.x + ENEMY_WIDTH));
    if ((this.enemyDirection > 0 && maxX + dx > CANVAS_WIDTH) || (this.enemyDirection < 0 && minX + dx < 0)) {
      this.enemyDirection *= -1;
      for (const enemy of alive) {
        enemy.y += ENEMY_DROP;
      }
    } else {
      for (const enemy of alive) {
        enemy.x += dx;
      }
    }
  }

  private enemyFire(): void {
    const alive = this.enemies.filter((e) => e.alive);
    if (alive.length === 0 || Math.random() > ENEMY_FIRE_CHANCE) {
      return;
    }
    const shooter = alive[Math.floor(Math.random() * alive.length)];
    this.enemyBullets.push({ x: shooter.x + ENEMY_WIDTH / 2, y: shooter.y + ENEMY_HEIGHT, vy: ENEMY_BULLET_SPEED });
  }

  private shoot(): void {
    if (this.status() !== 'playing') {
      return;
    }
    const now = Date.now();
    if (now - this.lastShotAt < SHOOT_COOLDOWN_MS) {
      return;
    }
    this.lastShotAt = now;
    this.playerBullets.push({ x: this.playerX + PLAYER_WIDTH / 2, y: PLAYER_Y, vy: -BULLET_SPEED });
  }

  private updateBullets(): void {
    this.playerBullets = this.playerBullets
      .map((bullet) => ({ ...bullet, y: bullet.y + bullet.vy }))
      .filter((bullet) => bullet.y > -10);
    this.enemyBullets = this.enemyBullets
      .map((bullet) => ({ ...bullet, y: bullet.y + bullet.vy }))
      .filter((bullet) => bullet.y < CANVAS_HEIGHT + 10);
  }

  private checkCollisions(): void {
    const spentPlayerBullets = new Set<Bullet>();
    for (const bullet of this.playerBullets) {
      for (const enemy of this.enemies) {
        if (!enemy.alive) {
          continue;
        }
        if (bullet.x >= enemy.x && bullet.x <= enemy.x + ENEMY_WIDTH && bullet.y >= enemy.y && bullet.y <= enemy.y + ENEMY_HEIGHT) {
          enemy.alive = false;
          spentPlayerBullets.add(bullet);
          this.score.update((s) => s + enemy.points);
          break;
        }
      }
    }
    this.playerBullets = this.playerBullets.filter((bullet) => !spentPlayerBullets.has(bullet));

    const spentEnemyBullets = new Set<Bullet>();
    for (const bullet of this.enemyBullets) {
      if (
        bullet.x >= this.playerX &&
        bullet.x <= this.playerX + PLAYER_WIDTH &&
        bullet.y >= PLAYER_Y &&
        bullet.y <= PLAYER_Y + PLAYER_HEIGHT
      ) {
        spentEnemyBullets.add(bullet);
        this.lives.update((l) => l - 1);
      }
    }
    this.enemyBullets = this.enemyBullets.filter((bullet) => !spentEnemyBullets.has(bullet));

    if (this.enemies.some((enemy) => enemy.alive && enemy.y + ENEMY_HEIGHT >= PLAYER_Y)) {
      this.lives.set(0);
    }
  }

  private checkEndConditions(): void {
    if (this.lives() <= 0) {
      this.status.set('lost');
    } else if (this.enemies.every((enemy) => !enemy.alive)) {
      this.status.set('won');
    }
  }

  private draw(): void {
    const ctx = this.ctx;
    if (!ctx) {
      return;
    }

    ctx.fillStyle = '#010c05';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    for (const enemy of this.enemies) {
      if (!enemy.alive) {
        continue;
      }
      ctx.fillStyle = enemy.color;
      ctx.fillRect(enemy.x, enemy.y, ENEMY_WIDTH, ENEMY_HEIGHT - 3);
      const legY = enemy.y + ENEMY_HEIGHT - 3;
      if (this.walkFrame) {
        ctx.fillRect(enemy.x + 2, legY, 3, 3);
        ctx.fillRect(enemy.x + ENEMY_WIDTH - 5, legY, 3, 3);
      } else {
        ctx.fillRect(enemy.x + 5, legY, 3, 3);
        ctx.fillRect(enemy.x + ENEMY_WIDTH - 8, legY, 3, 3);
      }
    }

    ctx.fillStyle = '#ffb000';
    for (const bullet of this.enemyBullets) {
      ctx.fillRect(bullet.x - 1, bullet.y, 2, 5);
    }

    ctx.fillStyle = '#33ff66';
    for (const bullet of this.playerBullets) {
      ctx.fillRect(bullet.x - 1, bullet.y - 5, 2, 5);
    }

    ctx.fillStyle = '#33ff66';
    ctx.fillRect(this.playerX, PLAYER_Y, PLAYER_WIDTH, PLAYER_HEIGHT);
    ctx.fillRect(this.playerX + PLAYER_WIDTH / 2 - 2, PLAYER_Y - 4, 4, 4);
  }
}
